import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";

/**
 * Admin panel: map departments → allowed modules. Backend has built-in
 * defaults; this table exposes the tenant-level override so admins never
 * have to touch the API.
 */
export default function DepartmentModulesPanel({ tenantModules = [] }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/department-modules");
      setData(data);
      const merged = { ...data.defaults, ...(data.overrides || {}) };
      // Show every dept in defaults, plus any override-only entries
      setRows(Object.entries(merged).map(([dept, mods]) => ({ dept, mods })));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const toggleModule = (deptIdx, module) => {
    setRows((r) => r.map((row, i) => {
      if (i !== deptIdx) return row;
      const has = row.mods.includes(module);
      return { ...row, mods: has ? row.mods.filter((m) => m !== module) : [...row.mods, module] };
    }));
  };

  const addRow = () => {
    const d = newDept.trim();
    if (!d) return;
    if (rows.find((r) => r.dept.toLowerCase() === d.toLowerCase())) {
      toast.error("Department already in the list");
      return;
    }
    setRows((r) => [...r, { dept: d, mods: [] }]);
    setNewDept("");
  };

  const removeRow = (dept) => setRows((r) => r.filter((row) => row.dept !== dept));

  const save = async () => {
    setSaving(true);
    try {
      const map = Object.fromEntries(rows.map((r) => [r.dept, r.mods]));
      await api.put("/tenant/department-modules", { map });
      toast.success("Department module map saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const modules = tenantModules.length ? tenantModules : ["finance", "hr", "scm", "hse", "eam", "analytics"];

  return (
    <section className="border border-zinc-950 mt-8" data-testid="dept-modules-panel">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
        <span>department → module access</span>
        <button onClick={load} className="text-white/60 hover:text-white flex items-center gap-1" title="Reload">
          <ArrowsClockwise size={11} />
        </button>
      </div>
      <div className="p-5">
        <p className="text-sm text-zinc-600 mb-4 max-w-2xl">
          Employees inherit the module list of their department when they don&apos;t have an explicit per-user override or custom role.
          Uncheck a module to revoke it for the department; unlisted modules fall through to the tenant defaults.
        </p>
        <div className="overflow-x-auto border border-zinc-200">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-950">
              <tr className="text-xs tracking-wider uppercase">
                <th className="text-left px-3 py-2">Department</th>
                {modules.map((m) => <th key={m} className="text-center px-2 py-2">{m}</th>)}
                <th />
              </tr>
            </thead>
            <tbody data-testid="dept-modules-tbody">
              {rows.map((row, i) => (
                <tr key={row.dept} className="border-b border-zinc-100">
                  <td className="px-3 py-2 font-medium">{row.dept}</td>
                  {modules.map((m) => (
                    <td key={m} className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={row.mods.includes(m)}
                        onChange={() => toggleModule(i, m)}
                        data-testid={`dept-${row.dept}-${m}`}
                        className="accent-[#FF3E00]"
                      />
                    </td>
                  ))}
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => removeRow(row.dept)} className="text-[10px] font-mono text-zinc-500 hover:text-[#D0021B]" data-testid={`dept-remove-${row.dept}`}>REMOVE</button>
                  </td>
                </tr>
              ))}
              <tr>
                <td className="px-3 py-2">
                  <input
                    value={newDept}
                    onChange={(e) => setNewDept(e.target.value)}
                    placeholder="Add new department…"
                    className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none w-full"
                    data-testid="new-dept-input"
                    onKeyDown={(e) => e.key === "Enter" && addRow()}
                  />
                </td>
                <td colSpan={modules.length + 1} className="px-3 py-2">
                  <button onClick={addRow} className="text-[10px] font-mono text-zinc-700 hover:text-[#FF3E00]" data-testid="add-dept-btn">+ ADD DEPARTMENT</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-dept-modules-btn"
            className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00] disabled:opacity-40 flex items-center gap-1"
          >
            <FloppyDisk size={12} /> {saving ? "SAVING…" : "SAVE OVERRIDES"}
          </button>
          {data && !data.is_default && (
            <span className="text-[10px] font-mono text-emerald-700">CUSTOM OVERRIDES ACTIVE</span>
          )}
        </div>
      </div>
    </section>
  );
}
